import React, { useEffect, useMemo, useState } from 'react';

import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import { ExpandSection, MenuCard } from '../../../components/molecules';
import { RemoveBasketItemType } from '../../../types/basket.d';
import { MenuActionsType, MenuSectionDataType } from '../../../types/menu.d';
import * as Api from '../../../weborder/Api';
import { Data, Item } from '../../../weborder/BasketControl';
import { menuUtils } from '../utils';

type MenuSectionProps = {
  actions: MenuActionsType;
  basket: Data;
  currency: Api.Currency;
  languageCode: string;
  expandedState: boolean;
  menuId: number;
  menuSectionData: MenuSectionDataType;
  translate: any;
  vrId: number;
};

const useStyles = makeStyles((theme: Theme) => ({
  menuSection: {
    margin: theme.spacing(4, 0, 0),
  },

  sectionTitle: {
    paddingTop: theme.spacing(4),

    '& [class*="MuiAccordionSummary-root MuiExpanded"]': {
      paddingTop: theme.spacing(4),
    },

    '&:before': {
      display: 'none',
    },
  },

  menuItems: {
    width: '100%',

    '& .fd-menucard:first-of-type': {
      borderTop: `1px solid ${theme.palette.grey[400]}`,
    },
  },

  menuItem: {
    boxSizing: 'border-box',
    width: '100%',
  },

  [theme.breakpoints.up('sm')]: {
    sectionTitle: {
      padding: theme.spacing(6, 3, 0),
    },

    menuItems: {
      columnCount: 2,
      columnGap: theme.spacing(2),
      columnWidth: 'auto',
      marginTop: theme.spacing(-1),
      paddingBottom: theme.spacing(2),

      '& .fd-menucard:first-of-type': {
        borderTop: 'none',
      },
    },

    menuItem: {
      breakInside: 'avoid',
      display: 'inline-block',
      marginBottom: theme.spacing(1),
      marginTop: theme.spacing(1),
      pageBreakInside: 'avoid',
      position: 'relative',
    },
  },

  [theme.breakpoints.up('md')]: {
    sectionTitle: {
      padding: 0,

      '& .MuiAccordionSummary-content, & .MuiAccordionSummary-expandIcon': {
        margin: theme.spacing(4, 0, 0),

        '&.Mui-expanded': {
          marginBottom: theme.spacing(2.5),
        },
      },
    },

    menuItems: {
      padding: 0,
      paddingBottom: theme.spacing(2),
    },
  },
}));

export function MenuSection(props: MenuSectionProps): JSX.Element {
  const {
    actions,
    basket,
    currency,
    languageCode,
    expandedState,
    menuId,
    menuSectionData,
    translate,
    vrId,
  } = props;

  const {
    description,
    expandBehaviour,
    imageUrl,
    items,
    name,
    sectionId,
  } = menuSectionData;

  const classes = useStyles();

  const theme = useTheme();
  const isTwoColumnMenuLayout = useMediaQuery(theme.breakpoints.up('sm'));

  const [expanded, setExpanded] = useState(null);

  const { handleAddItem, handleRemoveItem } = menuUtils;

  useEffect(() => {
    setExpanded(expandedState);
  }, [expandedState]);

  const toggleExpandedState = (): any => {
    if (!expanded) {
      if (expandBehaviour === Api.MenuSectionBehaviour.ExpandSingle) {
        actions.initExpanded(vrId, [sectionId]);
      } else {
        actions.onAddExpanded(vrId, sectionId);
      }
      const targetId = `menuSection-${sectionId.toString()}`;
      menuUtils.handleScrollToSection(targetId);
    } else {
      actions.onRemoveExpanded(vrId, sectionId);
    }
  };

  const reorderItemsForTwoColumnLayout = (menuItems) => {
    const column1 = [];
    const column2 = [];
    menuItems.forEach((item, index) =>
      index % 2 === 0 ? column1.push(item) : column2.push(item),
    );
    return [...column1, ...column2];
  };

  const renderMenuCard = (
    item: Api.MenuItem,
    basketCount: number,
  ): JSX.Element => {
    const basketIndex = basket?.items.findIndex(
      (basket) => basket.Item.MenuItemId === item.MenuItemId,
    );

    const onHandleItemClick = () => actions.onItemClick({ menuId, item });
    const onHandleAddItem = () => {
      const options = []; // item.MenuItemOptionSets?.MenuItemOptionSetItems
      const addItemData = {
        Item: item,
        Options: options,
      } as Item;

      return handleAddItem(actions.onAddItem, addItemData, menuId, vrId);
    };
    const onHandleRemoveItem = () => {
      const removeItemData = {
        index: basketIndex,
        item: item,
        menuId: menuId,
      } as RemoveBasketItemType;

      return handleRemoveItem(actions.onRemoveItem, removeItemData, vrId);
    };

    const menuCardActions = {
      onAddItem: onHandleAddItem,
      onClickItem: onHandleItemClick,
      onRemoveItem: onHandleRemoveItem,
    };

    return (
      <MenuCard
        actions={menuCardActions}
        basketCount={basketCount}
        className={classes.menuItem}
        currency={currency}
        key={item.MenuItemId}
        languageCode={languageCode}
        menuItem={item}
        translate={translate}
        vrId={vrId}
      />
    );
  };

  const getMenuCardDeck = (menuItems) => {
    return menuItems.map((item) => {
      const basketCount = basket?.items.filter(
        (basket) => basket.Item.MenuItemId === item.MenuItemId,
      ).length;

      return useMemo(() => renderMenuCard(item, basketCount), [
        item,
        basketCount,
      ]);
    });
  };

  const itemsOrderedForLayout = useMemo(() => {
    const displayOrderedMenuItems = items.sort(
      (a, b) => a.DisplayOrder - b.DisplayOrder,
    );

    return isTwoColumnMenuLayout
      ? reorderItemsForTwoColumnLayout(displayOrderedMenuItems)
      : displayOrderedMenuItems;
  }, [isTwoColumnMenuLayout]);

  const menuItemsDeck = getMenuCardDeck(itemsOrderedForLayout);

  return (
    <ExpandSection
      expanded={expanded}
      className={classes.sectionTitle}
      id={`menuSection-${sectionId.toString()}`}
      imageUrl={imageUrl}
      onChange={toggleExpandedState}
      sectionDescription={description}
      sectionName={name}
      TransitionProps={{
        mountOnEnter: true,
        timeout: 0,
        unmountOnExit: true,
      }}
      translate={translate}
    >
      <div className={classes.menuItems} role="list">
        {menuItemsDeck}
      </div>
    </ExpandSection>
  );
}
